/**
 * AI IP (Inventory Planning) Scenario Planning
 * IP 모듈 전용 — 현황 조회 · 시나리오 추천 · KPI 비교 · 확정
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box, Card, CardContent, Chip, CircularProgress,
  IconButton, LinearProgress, Stack, Tooltip, Typography,
} from "@mui/material";
import Inventory2Icon   from "@mui/icons-material/Inventory2";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon      from "@mui/icons-material/Refresh";
import scenarioService  from "../../services/ai/scenarioService";
import { rememberModuleCatalog } from "./scenarioParamUtil";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import { StatusSection, StatusKpiSection, NavName, ForecastActualChart,
         StatusItemHeader, StatusItemRow, StatusItemList, statusListH, StatusDistributionBar,
         StatusEmpty, StatusEmptyPanel, StatusPendingTag, buildStatusPanelSummary, BarTip,
         openPlanRunScreen, useLastRunSummary,
         StatusToolbar, StatusToolbarSelect, STATUS_TOOLBAR_BLOCK_H } from "./scenarioStatusUi";
import i18n from "@plannel/utils/i18n";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { FeasibilityHeroBadges, feasWarnCount } from "./CompareFeasibilityWarnings";
import { fmtMoney } from "./aiMoney";

// 현황 드릴다운 → 목표재고 시뮬레이션 결과 화면(버전 + 거점/품목/품목계층 자동 조회)
// cond: { locCd?, itemCd?, categoryCd?, categoryName? }
export function openTargetInvSimulationResults(dispatch, cond, version) {
  const { locCd, itemCd, categoryCd, categoryName } = cond || {};
  // 버전만 지정(시나리오 산점도 클릭)해도 오픈 허용 — 필터 없이 해당 버전 전체 결과 표시.
  if (!locCd && !itemCd && !categoryCd && version?.id == null) return;    // 식별 불가 시 무시
  const VIEW = "TARGET_INV_SIMULATION_RESULTS";
  const payload = { currentPage: 1 };
  if (version?.id != null) {
    payload.ipVersion = { id: version.id, name: String(version.name ?? "") };
  }
  const filter = { locations: null, category: null };
  if (locCd) filter.locations = [{ locCd: String(locCd), locName: String(locCd) }];
  if (categoryCd) filter.category = [{ hrchyCd: String(categoryCd), name: String(categoryName ?? categoryCd), leafFlg: true }];
  payload.filter = filter;
  if (itemCd) payload.itemSearch = String(itemCd);
  dispatch(reduxUtil.removeViewState({ viewName: VIEW }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(VIEW, payload)));
  dispatch(reduxUtil.updateHistoryState(VIEW));
}

// ── 색상 팔레트 ────────────────────────────────────────────────────────────
const C = {
  primary: "#185FA5", primaryBg: "#E6F1FB", primaryL: "#B5D4F4",
  success: "#1D9E75", successBg: "#E1F5EE", successBd: "#5DCAA5",
  warning: "#BA7517", warningBg: "#FAEEDA", warningBd: "#EF9F27",
  danger:  "#E24B4A", dangerBg:  "#FCEBEB", dangerBd:  "#FCA5A5",
  neutral: "#9CA3AF", neutralBg: "#F1F4F8", neutralBd: "#E5E7EB",
  text1:   "#111827", text2: "#4B5563", text3: "#9CA3AF",
  border:  "#E5E7EB", bg2: "#FFFFFF", bg3: "#F1F4F8",
};

// label 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처에서 t(cfg.label) 형태로 변환한다.
const KPI_STATUS_CFG = {
  good:    { label: "kpiAchievedLabel",   color: C.success, bg: C.successBg, border: C.successBd },
  near:    { label: "kpiNearLabel",       color: C.primary, bg: C.primaryBg, border: C.primaryL  },
  warn:    { label: "warning",            color: C.warning, bg: C.warningBg, border: C.warningBd },
  bad:     { label: "kpiTargetMissLabel", color: C.danger,  bg: C.dangerBg,  border: C.dangerBd  },
  neutral: { label: "—",                 color: C.neutral, bg: C.neutralBg, border: C.neutralBd },
};

// KPI 수치 포매팅 — 모든 수치는 소수 1자리까지, 소수부 없으면 정수로 표기.
const n1 = (v) => {
  const r = Math.round(Number(v) * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};
// ⚠️ 값이 없으면 null 을 반환한다 — 미집계 표기('—', 단위·배지·상태색 제거)는
//    StatusKpiCard 가 소유한다. 0 은 **실측값**이므로 정상 포맷한다(구버전은 0 도 "-"로
//    찍어 실측 0 을 미집계처럼 보이게 했다 — 방향이 반대인 버그였다).
const fmtDays = (v, t) => v == null ? null : `${n1(v)}${t ? t("dayUnit") : "일"}`;
const fmtPct  = (v) => v == null ? null : `${n1(v)}%`;
const fmtAmt  = fmtMoney;
const fmtCnt = (v, t) => v == null ? null : `${Number(v).toLocaleString()}${t ? t("caseUnitLabel") : "건"}`;
// KPI 카드 값 전용 — 단위는 StatusKpiCard 의 unit 슬롯이 소유한다(값 28px / 단위 14px).
//   위 fmt* 는 단위를 붙인 한 덩어리라 footer·배지처럼 본문 크기로 쓰는 자리에 그대로 남긴다.
const kpiNum = (v) => (v == null ? null : n1(v));
const kpiCnt = (v) => (v == null ? null : Number(v).toLocaleString());
// 계층 분포 바 수량 — 세그먼트 라벨/합계는 축약 없이 원값(EA·품목 수 비교가 목적)
const fmtDistQ = (v) => Math.round(Number(v || 0)).toLocaleString();
// 문제항목 카드 현재/목표 수량용 콤팩트 포맷(단위 없음, 큰 값은 K/M 축약)
const fmtQ = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `${n1(n / 1e6)}M`;
  if (Math.abs(n) >= 1e4) return `${n1(n / 1e3)}K`;
  return Math.round(n).toLocaleString();
};

// SL 상태
function slStatus(pct) {
  if (pct == null) return "neutral";   // 미집계 — 판정 없음(빨강으로 떨어지면 안 된다)
  if (pct >= 95) return "good";
  if (pct >= 90) return "near";
  if (pct >= 85) return "warn";
  return "bad";
}
// 재고일수 상태 (목표 대비)
function invDaysStatus(days, target) {
  if (!days || !target) return "neutral";
  const ratio = days / target;
  if (ratio > 1.3 || ratio < 0.7) return "bad";
  if (ratio > 1.15 || ratio < 0.85) return "warn";
  return "good";
}
// 재고 보유 금액 상태 (목표 금액 대비 현재 보유액 — 재고일수와 동일 밴드)
function invAmtStatus(amt, target) {
  if (!amt || !target) return "neutral";
  const ratio = amt / target;
  if (ratio > 1.3 || ratio < 0.7) return "bad";
  if (ratio > 1.15 || ratio < 0.85) return "warn";
  return "good";
}

// 리스크 상태 칩
const RISK_COLORS = {
  CRITICAL: { color: C.danger,  bg: C.dangerBg  },
  HIGH:     { color: C.warning, bg: C.warningBg },
  MEDIUM:   { color: C.primary, bg: C.primaryBg },
  LOW:      { color: C.success, bg: C.successBg },
};
// 값은 i18n 키 문자열 — 소비처(Chip label)에서 t(RISK_LABEL[cd]) 형태로 변환한다.
const RISK_LABEL = { CRITICAL: "riskLevelCriticalLabel", HIGH: "riskLevelHighLabel", MEDIUM: "riskLevelMediumLabel", LOW: "riskLevelLowLabel" };

// 현황 리스트 우측 위험도 칩 열 폭 — 실측 동기화(StatusItemList) 전 첫 페인트용 폴백값.
// 실제 폭은 전 행의 칩 실측 최대값(위험/높음/Critical 등 그 언어 실폭)으로 맞춰진다.
const RISK_CHIP_W = 68;
// 재고 이슈 진단 행 — 수치 셀 폭(최장 "3.5%p") / 우측 KPI 칩 열 폭(최장 "서비스 레벨")
const ISSUE_CNT_W     = 64;
const ISSUE_KPI_CHIP_W = 92;

// ── 이슈 스타일 (RP AiRpScenarioPlanning.js 패턴) ───────────────────────────
// 문제 항목/과잉재고 섹션(RISK_COLORS)과 색감 통일 — 선명한 단일 강조색(바·칩·숫자 공용) + 연한 배경.
const ISSUE_STYLE = {
  error:   { color: C.danger,  bg: C.dangerBg,  text: "#791F1F" },
  warning: { color: C.warning, bg: C.warningBg, text: "#633806" },
  info:    { color: C.primary, bg: C.primaryBg, text: "#0C447C" },
};
// 좌측 추이 차트 높이를 우측 이슈 진단 목록의 실측 높이에 맞춘다(둘 다 height:100% 라
// grid row 는 큰 쪽에 맞춰지는데, 차트 ResponsiveContainer 는 고정 높이라 이슈가 길면
// 차트 아래에 빈 공간이 생긴다). 카드 높이는 desc 줄바꿈·화면폭·언어에 따라 달라져
// 상수 추정이 어긋나므로 ResizeObserver 로 이슈 목록 컨테이너 높이를 직접 잰다. (RP 와 동일)
//   IP_CHART_PAD_DIFF_PX : 차트 content 패딩(14×2) − 이슈 content 패딩(10×2). 두 카드의
//                          자연 높이를 맞추려면 차트 내부를 이슈 목록보다 이만큼 줄인다.
//   IP_CHART_MIN_H       : 이슈가 적거나 없을 때의 하한 — 이슈 카드 4개짜리 목록 높이 기준
//                          (BF 모델 분포 차트가 4행을 하한으로 잡는 것과 같은 규칙)
const IP_CHART_PAD_DIFF_PX = 8;
const IP_CHART_MIN_H = statusListH(4) - IP_CHART_PAD_DIFF_PX;
// ── 목표 근접도(개선/악화) 판정 ──────────────────────────────────────────────
// mode 별 '목표까지 거리':
//   "floor"  서비스레벨 — 목표는 하한(높을수록 양호). 목표 이상이면 거리 0.
//   "lower"  과잉재고   — 0이 이상(낮을수록 양호). target=0, 거리=값.
//   "two"    재고일수·보유금액 — 목표=이상치, 양방향 이탈 모두 악화. 거리=|값-목표|.
function targetDist(val, target, mode) {
  if (val == null || target == null) return null;
  if (mode === "floor") return Math.max(0, target - val);
  if (mode === "lower") return Math.max(0, val);
  return Math.abs(val - target);
}
// 릴리즈 대비 '목표에 가까워졌나' — improve(가까워짐)/worsen(멀어짐)/neutral(동일·판정불가).
function proximityState(cur, rel, target, mode) {
  const dCur = targetDist(cur, target, mode);
  const dRel = targetDist(rel, target, mode);
  if (dCur == null || dRel == null) return "neutral";
  if (dCur < dRel) return "improve";
  if (dCur > dRel) return "worsen";
  return "neutral";
}

// 차이 배지(RP 디자인) — delta만 컬러. "{prefix} ±N ▲/▼ {note}".
// 색 결정 두 경로:
//  1) state 명시("improve"/"worsen"/"neutral") — 외부 판정(릴리즈 근접도)으로 색·문구 결정.
//  2) higherWorse(기본) — delta 방향으로 양호/악화. false=오를수록 양호(서비스레벨),
//     true=오를수록 악화(재고일수·보유비용). badTone: 악화 색({c,bg}, 기본 red).
// bg: 알약 배경 오버라이드(미지정 시 색 톤 연배경). prefix: i18n 키 문자열("vsTargetLabel"/"vsReleaseLabel").
// okNote/badNote 도 i18n 키 문자열(caller 가 전달) — 이 함수 내부에서 t()로 변환한다.
function GapBadge({ delta, fmt, prefix, higherWorse, okNote, badNote,
                   badTone = { c: C.danger, bg: C.dangerBg }, state, bg }) {
  const { t } = useTranslation();
  if (delta == null || !isFinite(delta)) return null;
  const zero = delta === 0;
  let c, tone, note;
  if (state != null) {
    c    = state === "improve" ? C.success   : state === "worsen" ? C.danger   : C.text2;
    tone = state === "improve" ? C.successBg : state === "worsen" ? C.dangerBg : C.bg3;
    note = zero ? t("noChangeLabel") : state === "improve" ? t("improvementLabel") : state === "worsen" ? t("worsenedLabel") : null;
  } else {
    const good = zero ? true : (higherWorse ? delta < 0 : delta > 0);
    const toneObj = good ? { c: C.success, bg: C.successBg } : badTone;
    c = toneObj.c; tone = toneObj.bg;
    note = zero ? t("kpiAchievedLabel") : good ? (okNote ? t(okNote) : null) : (badNote ? t(badNote) : null);
  }
  // 변화 없음(zero)이면 단위(0일/0%p) 생략 — prefix + note만.
  const sign     = delta > 0 ? "+" : delta < 0 ? "-" : "";
  const arrow    = zero ? "" : delta > 0 ? " ▲" : " ▼";
  const valueStr = zero ? "" : `${sign}${fmt(Math.abs(delta))}${arrow}`;
  const prefixText = prefix ? t(prefix) : "";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center",
               px: 0.6, py: 0.1, borderRadius: 1, bgcolor: bg ?? tone, flexShrink: 0 }}>
      <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: c, lineHeight: 1.4,
                        whiteSpace: "nowrap" }}>
        {prefixText}{valueStr ? ` ${valueStr}` : ""}{note ? ` ${note}` : ""}
      </Typography>
    </Box>
  );
}

// ── IP 현황 조회 전용 컴포넌트 ─────────────────────────────────────────────
export function IpExtraInitContent({ versionInfo, cachedData, onDataChange, onReadyChange }) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [data,    setData]    = useState(cachedData ?? null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState(null);
  const [trendGroup, setTrendGroup] = useState("");   // "" = 전사 / leaf_cd = Leaf 품목 계층
  // 계층 분포 바에서 고른 Leaf 계층 — 바로 아래 TOP5 리스트를 그 계층으로 좁힌다.
  //   부족·과잉은 **서로 독립**이다(한쪽 계층이 다른 쪽 분포에 없을 수 있어 공용 선택은 한쪽을 비운다).
  const [shortageLeaf, setShortageLeaf] = useState(null);   // {cd, name} | null
  const [excessLeaf,   setExcessLeaf]   = useState(null);

  // 대상 화면이 이 테넌트/사용자 권한 밖이면 드릴다운 링크를 만들지 않는다
  //   (NavName 은 onClick 이 함수가 아닌 항목을 스스로 걸러낸다).
  const canDrillSim = isPermitted("TARGET_INV_SIMULATION_RESULTS");

  // 현황 드릴다운 → 목표재고 시뮬레이션 결과(버전 + 거점/품목 또는 품목계층)
  const goToSimByItem = useCallback(
    (row) => openTargetInvSimulationResults(reduxDispatch, { locCd: row.loc_cd, itemCd: row.item_cd }, versionInfo),
    [reduxDispatch, versionInfo],
  );
  // seg = 계층 분포 바 세그먼트 {cd, name}. '기타' 세그먼트는 cd 가 없어 호출되지 않는다.
  //   부족·과잉 바는 화면 내 필터로 쓰고, 대응 리스트가 없는 '적정 재고' 바만 이 드릴다운을 유지한다.
  const goToSimByCategory = useCallback(
    (seg) => openTargetInvSimulationResults(reduxDispatch, { categoryCd: seg?.cd, categoryName: seg?.name }, versionInfo),
    [reduxDispatch, versionInfo],
  );
  // 같은 세그먼트를 다시 누르면 해제(전체로 복귀)
  const toggleLeaf = useCallback((setter) => (seg) => {
    setter(prev => (prev?.cd === seg?.cd ? null : { cd: seg?.cd, name: seg?.name }));
  }, []);

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    scenarioService.getIpInitialState()
      .then(r => setData(r))
      .catch(e => setErr(e?.response?.data?.detail || e.message || t("MSG_IpQueryError")))
      .finally(() => setLoading(false));
  }, [t]);

  // 캐시가 없을 때만 조회
  useEffect(() => {
    if (cachedData) return;
    load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // 데이터 변경 시 부모에 캐시 전달
  useEffect(() => {
    if (data) onDataChange?.(data);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // 재조회로 데이터셋이 바뀌면 계층 선택을 푼다 — 새 데이터에 없는 계층이 선택된 채로 남으면
  // 칩은 붙어 있는데 리스트만 비어 "데이터가 없다"로 오독된다.
  useEffect(() => {
    setShortageLeaf(null);
    setExcessLeaf(null);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // 조회 완료 여부를 부모에 보고 → 상단 액션바(자동 수행 / 시나리오 구성으로) 잠금 해제 기준.
  //   실패(err)도 완료로 본다 — 데이터가 안 와도 사용자가 버튼에 갇히면 안 된다(재시도는 아래 버튼).
  //   마운트 직후 loading=false 구간을 "완료"로 오판하지 않도록 data/err 도착까지 false 를 유지한다.
  useEffect(() => {
    onReadyChange?.(!loading && (!!data || !!err));
  }, [loading, data, err]); // eslint-disable-line react-hooks/exhaustive-deps

  // 최근 실행 기록(빈 상태 패널 우측 요약) — 전 모듈 공용 훅. 미집계일 때만 1건 조회한다.
  //   ⚠️ 훅이라 아래 loading/err early return 보다 앞에 있어야 한다(훅 순서 규칙).
  const lastRun = useLastRunSummary("IP", data?.released_fact_available === false);

  // 월별 재고 추이 — 전사/Leaf 계층을 드롭다운으로 로컬 전환 (재조회 없음)
  const selectedTrend = useMemo(() => {
    const td = data?.inventory_trend;
    if (!td) return null;
    return trendGroup ? (td.by_group?.[trendGroup] ?? { data: [], series: [] }) : td.all;
  }, [data, trendGroup]);

  // 좌측 추이 차트 높이 = 우측 이슈 목록 컨테이너 실측 높이 − 패딩차(하한 IP_CHART_MIN_H).
  // 콜백 ref 로 이슈 목록 Box 를 ResizeObserver 에 물려 개수·줄바꿈 변화에 자동 추종한다. (RP 와 동일)
  // ⚠️ 아래 loading/err early return 보다 앞에 둬야 훅 호출 순서가 고정된다(rules-of-hooks).
  const [chartTrendHeight, setChartTrendHeight] = useState(IP_CHART_MIN_H);
  const issueRoRef = useRef(null);
  const measureIssueBox = useCallback((el) => {
    if (issueRoRef.current) { issueRoRef.current.disconnect(); issueRoRef.current = null; }
    if (!el) return;
    const update = () => setChartTrendHeight(
      Math.max(IP_CHART_MIN_H, Math.round(el.offsetHeight - IP_CHART_PAD_DIFF_PX)),
    );
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    issueRoRef.current = ro;
  }, []);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: "16px !important" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: C.primary }} />
            <Typography variant="caption" color="text.secondary">
              {t("MSG_IpStatusAnalyzing")}
            </Typography>
          </Stack>
          <LinearProgress sx={{ mt: 1.5, borderRadius: 2, height: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.danger}30` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <WarningAmberIcon sx={{ fontSize: 15, color: C.warning }} />
            <Typography variant="caption" color={C.warning}>{t("MSG_IpStatusQueryFailed", { err })}</Typography>
            <IconButton size="small" onClick={load} sx={{ ml: "auto !important", p: "3px" }}>
              <RefreshIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // 표준 initial-state 골격(flat): kpi_current/risk_summary/issue_summary 최상위
  const cs           = data              || {};
  // ⚠️ 릴리즈 fact 미적재(미집계)면 **화면 전체를 일괄 미집계로 취급**한다.
  //   IP 는 릴리즈와 무관한 실측 계열(현재 보유금액·수요 추세)을 따로 읽는데, 그것만 남겨두면
  //   "어떤 카드는 값이 있고 어떤 카드는 —" 인 반쪽 화면이 되어 판정 가능 여부를 오독하게 만든다.
  //   판정의 기준선(목표재고)이 없는 이상 실측값도 해석 근거가 없으므로 함께 감춘다.
  const releasedNone = cs.released_fact_available === false;
  const kpi          = releasedNone ? {} : (cs.kpi_current    || {});
  const risk         = releasedNone ? {} : (cs.risk_summary   || {});
  const issue        = releasedNone ? {} : (cs.issue_summary  || {});
  // 수요 추세(전사 월별 총량 OLS 기울기) — 실측이지만 위 원칙에 따라 미집계면 함께 감춘다.
  const dv           = releasedNone ? {} : (cs.demand_trend || {});
  // 품목 Leaf 분류 단위 부족·과잉·적정 분포 — 백엔드가 상위 9 + '기타'로 절단해 내려준다
  //   (기준: 릴리즈 fact. 위 TOP5 리스트의 '현재 실적' 기준과 시점이 다르다)
  const hrchyDist   = releasedNone ? {} : (cs.item_hrchy_dist || {});
  const distSegs    = (k) => (Array.isArray(hrchyDist[k]?.segments) ? hrchyDist[k].segments : []);
  const distShortage = distSegs("shortage");
  const distExcess   = distSegs("excess");
  const distNormal   = distSegs("normal");
  // 3버킷 분류 기준(목표 대비 ±band%) — 세 바가 같은 기준을 공유하므로 툴팁도 하나로 공용
  const distBandTip  = t("itemHrchyBandTip", { band: n1(hrchyDist.band_pct ?? 10) });
  // 선택된 계층 표시 칩 — 리스트 제목 옆에 붙어 "지금 무엇으로 좁혀져 있는지"와 해제 수단을 겸한다
  const leafChip = (leaf, clear) => (leaf ? (
    <Chip
      size="small"
      label={leaf.name || leaf.cd}
      onDelete={clear}
      sx={{ height: 19, fontSize: FONT.CAPTION, maxWidth: 180,
            bgcolor: `${C.acc}14`, color: C.acc, border: `0.5px solid ${C.acc}55`,
            "& .MuiChip-label": { px: "6px" },
            "& .MuiChip-deleteIcon": { fontSize: 13, color: C.acc } }}
    />
  ) : null);
  // 월별 실제재고 곡선 + 목표재고 기준선. 전사 데이터 존재 여부로 섹션 노출 판단.
  //   미집계면 목표재고 기준선이 없어 곡선만 남는다 → 위 원칙대로 함께 감춘다(스켈레톤 표시).
  const invHasData = !releasedNone && (cs.inventory_trend?.all?.data?.length || 0) > 0;
  // Leaf 품목 계층 드롭다운 옵션 (leaf_cd → leaf_name), 이름 정렬
  const invGroupLabels = cs.inventory_trend?.group_labels || {};
  const invLeafOptions = Object.entries(invGroupLabels)
    .sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  // 툴바(조회 범위) 행이 본문 최상단을 차지한 만큼 차트를 줄여, 우측 이슈 카드와 총 높이를 맞춘다.
  const trendToolbarH = invLeafOptions.length > 0 ? STATUS_TOOLBAR_BLOCK_H : 0;

  // 릴리즈 KPI — 미집계면 null 을 유지한다(0 으로 접으면 실측 0 과 구분되지 않는다).
  const avgInvDays    = kpi.avg_inv_days         ?? null;
  const avgTargetDays = kpi.avg_target_inv_days  ?? null;
  const avgSlPct      = kpi.avg_effective_sl_pct ?? null;
  // issue_summary 카운트는 릴리즈 fact 기반이라 미집계면 0 이 내려온다(신호 카운트라 백엔드는
  // 0 을 유지한다). KPI 카드 폴백으로 쓰면 "과잉 0건"이 판정처럼 보이므로 여기서 끊는다.
  const excessCnt     = releasedNone ? null : (issue.excess_inv_count ?? null);
  const totalItems    = issue.total_items        ?? 0;

  // 현재(최신 실적) 번들 — 최종 컷오프(일별 원장 최신 시점) 기준 현황. 없으면 릴리즈 폴백.
  //   미집계면 이 번들도 통째로 비운다(위 일괄 미집계 원칙) — 목표 없는 실측만 남기지 않는다.
  const actual       = releasedNone ? {} : (cs.actual_inv || {});
  const curInvDays   = actual.current_avg_inv_days;   // null/undefined면 폴백/배지 미표시
  const curExcessCnt = actual.current_excess_count;
  const curInvAmt    = actual.current_total_inv_amt;
  const relInvAmt    = actual.released_total_inv_amt;  // 릴리즈 실적 보유금액(변동 비교 기준)
  const curSlPct     = actual.current_sl_pct;          // 안전재고 충족률(%)
  const curRisk      = actual.current_risk_summary;
  const curIssue     = actual.current_issue_summary;
  const curHighlights = actual.current_highlights;

  // 현황 탭 전반의 표시 기준값 — 현재(최신) 우선, actual_inv 없으면 릴리즈로 폴백
  const hasActual    = Array.isArray(curHighlights);
  const effRisk      = curRisk  || risk;
  const effIssue     = curIssue || issue;
  const effSlPct     = curSlPct != null ? curSlPct : avgSlPct;
  const effInvDays   = curInvDays != null ? curInvDays : avgInvDays;
  const effExcessCnt = curExcessCnt != null ? curExcessCnt : excessCnt;
  const effTotal     = effIssue.total_items ?? totalItems;
  const stockoutCnt  = effIssue.stockout_count ?? 0;
  const critCnt      = effRisk.CRITICAL ?? 0;

  // ── 주요 이슈/문제점 + 부족/과잉 TOP 5 (현재 기준 highlights, 없으면 릴리즈) ──
  // 계층 선택 시에는 백엔드가 같은 정렬로 잘라둔 **계층별 TOP5 맵**을 소스로 바꾼다.
  //   전역 리스트를 필터하면 안 된다 — 전역 5행 안에 그 계층 항목이 없을 수 있다.
  //   실적/릴리즈 폴백(hasActual) 규칙은 전역 리스트와 동일하게 유지한다.
  const byHrchy = (leaf, actKey, relKey) => {
    if (!leaf?.cd) return null;
    const map = (hasActual ? actual[actKey] : cs[relKey]) || {};
    return Array.isArray(map[leaf.cd]) ? map[leaf.cd] : [];
  };
  const highlights  = releasedNone ? [] : (hasActual ? curHighlights : (cs.highlights || []));
  // 부족 금액 큰 순(inv_gap_amt 가장 음수 우선) → 통일 정렬 기준
  const shrtgeSrc   = releasedNone ? []
    : (byHrchy(shortageLeaf, "current_highlights_by_hrchy", "highlights_by_hrchy") ?? highlights);
  const shrtgeItems = (Array.isArray(shrtgeSrc) ? shrtgeSrc : [])
    .filter(h => h.shrtge_inv_flg === "Y" || (h.inv_gap_amt ?? 0) < 0)
    .sort((a, b) => (a.inv_gap_amt ?? 0) - (b.inv_gap_amt ?? 0))
    .slice(0, 5);
  // 과잉재고 TOP 5 — 백엔드 과잉 전용 리스트(excess_highlights) 사용. highlights 는
  // 부족 전용이므로 폴백 없음. 과잉 금액 큰 순(inv_gap_amt 가장 양수 우선)으로 정렬.
  const excessHl    = releasedNone ? [] : (hasActual ? actual.current_excess_highlights : cs.excess_highlights);
  const excessSrc   = releasedNone ? []
    : (byHrchy(excessLeaf, "current_excess_highlights_by_hrchy", "excess_highlights_by_hrchy") ?? excessHl);
  const excessItems = (Array.isArray(excessSrc) ? excessSrc : [])
    .slice()
    // 백엔드와 같은 2키 정렬(금액 → 초과일수) — 1키만 쓰면 동점에서 순서가 백엔드와 갈린다
    .sort((a, b) => (b.inv_gap_amt ?? 0) - (a.inv_gap_amt ?? 0)
                 || (b.excess_days ?? 0) - (a.excess_days ?? 0))
    .slice(0, 5);

  // rule-based 이슈 카드 — 현재(최신) 기준값 사용(actual 없으면 릴리즈 폴백)
  const slLabel = t("avgServiceLevelLabel");  // 현재/릴리즈 모두 동일 엔진 effective SL 기준
  const issueItems = [];
  if (stockoutCnt > 0 || critCnt > 0) {
    const critPart = critCnt ? t("MSG_IpStockoutCriticalSuffix", { count: critCnt }) : "";
    issueItems.push({
      level: "error", title: t("stockoutIssueTitle"), kpiLabel: t("safetyStockLabel"),
      desc: t("MSG_IpStockoutIssueDesc", { count: stockoutCnt, critPart }),
      affectedCount: stockoutCnt || critCnt, unit: t("caseUnitLabel"),
    });
  }
  if (effExcessCnt > 0) {
    issueItems.push({
      level: "warning", title: t("excessInvIssueTitle"), kpiLabel: t("invDaysLabel"),
      desc: t("MSG_IpExcessIssueDesc", { count: effExcessCnt }),
      affectedCount: effExcessCnt, unit: t("caseUnitLabel"),
    });
  }
  if (effSlPct > 0 && effSlPct < 95) {
    const slGap = +(95 - effSlPct).toFixed(1);
    issueItems.push({
      level: slGap > 5 ? "warning" : "info", title: t("slMissIssueTitle"), kpiLabel: t("serviceLevelLabel"),
      desc: t("MSG_IpSlMissDesc", { slLabel, pct: effSlPct, gap: slGap }),
      affectedCount: slGap, unit: "%p",
    });
  }
  if (effInvDays && avgTargetDays && effInvDays > avgTargetDays) {
    const gapDays = +(effInvDays - avgTargetDays).toFixed(1);
    issueItems.push({
      level: "info", title: t("invDaysOverTargetIssueTitle"), kpiLabel: t("invDaysLabel"),
      desc: t("MSG_IpInvDaysOverTargetDesc", { days: effInvDays, target: avgTargetDays, gap: gapDays }),
      affectedCount: gapDays, unit: t("dayUnit"),
    });
  }
  // 수요 추세 — 전사 월별 수요 총량의 OLS 회귀 기울기. 실적(과거 판매) 기반.
  const dvGrowth = dv.monthly_growth_pct != null ? Number(dv.monthly_growth_pct).toFixed(1) : "-";
  const dvChange = dv.change_pct != null ? Number(dv.change_pct).toFixed(1) : "-";
  if (dv.trend === "up") {
    const changePart = dv.change_pct != null ? t("ipCumulativeChangeUpSuffix", { change: dvChange }) : "";
    issueItems.push({
      level: "warning",
      title: t("demandTrendUpTitle"),
      kpiLabel: t("demandTrendLabel"),
      desc: t("MSG_IpDemandTrendUpDesc", { growth: dvGrowth, changePart }),
      affectedCount: dv.change_pct != null ? dvChange : dvGrowth, unit: "%",
    });
  } else if (dv.trend === "down") {
    const changePart = dv.change_pct != null ? t("ipCumulativeChangeSuffix", { change: dvChange }) : "";
    issueItems.push({
      level: "info",
      title: t("demandTrendDownTitle"),
      kpiLabel: t("demandTrendLabel"),
      desc: t("MSG_IpDemandTrendDownDesc", { growth: dvGrowth, changePart }),
      affectedCount: dv.change_pct != null ? dvChange : dvGrowth, unit: "%",
    });
  } else if (dv.trend === "flat") {
    const changePart = dv.change_pct != null ? t("ipCumulativeChangeSuffix", { change: dvChange }) : "";
    issueItems.push({
      level: "info",
      title: t("demandTrendFlatTitle"),
      kpiLabel: t("demandTrendLabel"),
      desc: t("MSG_IpDemandTrendFlatDesc", { growth: dvGrowth, changePart }),
      affectedCount: dv.change_pct != null ? dvChange : dvGrowth, unit: "%",
    });
  }


  // KPI 카드 데이터 — 본값은 '현재(최신 실적)'. 현재값 없으면 릴리즈값으로 폴백.
  // 릴리즈값은 footerRight 보조 배지로 비교 기준 노출.
  const hasCurInvDays = curInvDays != null;
  const dispInvDays   = hasCurInvDays ? curInvDays : avgInvDays;       // 본값(현재 우선)
  const hasCurExcess  = curExcessCnt != null;
  const dispExcess    = hasCurExcess ? curExcessCnt : excessCnt;
  const hasCurAmt     = curInvAmt != null;
  const dispInvAmt    = hasCurAmt ? curInvAmt : kpi.total_target_inv_amt;
  const hasCurSl      = curSlPct != null;
  const dispSlPct     = hasCurSl ? curSlPct : avgSlPct;

  // ── 비교 표기 빌더 ─────────────────────────────────────────────────────────
  // footerLeft(좌) = tgtBadge: "목표 X"(평문) + 목표 대비 delta 컬러 배지.
  // footerRight(우) = relBadge: "릴리즈 X"(평문) + 릴리즈 대비 delta 컬러 배지(동일 디자인, 흰 배경).
  //   delta = 현재-릴리즈, 개선/악화 = 목표 근접도(proximityState). gapFmt: |delta| 단위 포매터.
  // ⚠️ 미집계(릴리즈 결과 없음)면 비교 기준 자체가 없다 — 값이 0 으로 내려오더라도 배지를 만들지
  //    않는다. (백엔드 COALESCE 로 0 이 새어 들어오던 "릴리즈 대비 +NNN ▲" 거짓 델타 차단)
  const relBadge = (cur, rel, target, mode, fmt, gapFmt = fmt) => {
    if (releasedNone) return null;
    if (cur == null || rel == null) return null;
    const delta = cur - rel;
    const st = delta === 0 ? "neutral" : proximityState(cur, rel, target, mode);
    return (
      <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 0.2,
                 flexShrink: 0 }}>
        <Typography sx={{ fontSize: FONT.BODY, color: C.text2, fontWeight: 500, lineHeight: 1.4,
                          px: 0.6, whiteSpace: "nowrap" }}>
          {t("finalReleaseBasisValueLabel", { value: fmt(rel) })}
        </Typography>
        <GapBadge delta={delta} fmt={gapFmt} prefix="vsReleaseLabel" state={st} bg="transparent" />
      </Box>
    );
  };

  const kpiCards = [
    (() => {
      const status = invDaysStatus(dispInvDays, avgTargetDays);
      // 초과 배지 색을 카드 status 밴드와 일치(good→초록·warn→주황·bad→빨강).
      const dCfg = KPI_STATUS_CFG[status] || KPI_STATUS_CFG.neutral;
      return {
        icon: "", label: t("avgInvDaysLabel"),
        value: kpiNum(dispInvDays), unit: t("dayUnit"), status,
        valueRight: (curInvDays != null && avgTargetDays)
          ? <GapBadge delta={curInvDays - avgTargetDays} higherWorse fmt={(v) => `${n1(v)}${t("dayUnit")}`}
                      okNote="shortenedLabel" badNote="exceededLabel" badTone={{ c: dCfg.color, bg: "transparent" }} bg="transparent" />
          : null,
        footer: avgTargetDays ? t("targetValueLabel", { value: fmtDays(avgTargetDays, t) }) : "",
        footerRight: relBadge(curInvDays, avgInvDays, avgTargetDays, "two", (v) => fmtDays(v, t), (v) => `${n1(v)}${t("dayUnit")}`),
      };
    })(),
    (() => {
      // 현황 SL = 엔진 effective SL을 현재 on-hand로 재계산 → 릴리즈 SL과 동일 기준 비교 가능.
      // 목표 95% 하한(floor): 현재·릴리즈 모두 95↑이면 거리 0 → 개선/악화 중립.
      const status = slStatus(dispSlPct);
      // 미달 배지 색을 카드 status 밴드와 일치(near→파랑, warn→주황, bad→빨강).
      // 카드 배경이 파랑(근접)인데 배지만 빨강(미달)으로 보이는 색 모순 제거.
      const slCfg = KPI_STATUS_CFG[status] || KPI_STATUS_CFG.neutral;
      return {
        icon: "", label: t("serviceLevelSpacedLabel"),
        value: kpiNum(dispSlPct), unit: t("percentUnit"), status,
        valueRight: (hasCurSl && curSlPct != null)
          ? <GapBadge delta={curSlPct - 95} higherWorse={false} fmt={(v) => `${n1(v)}%p`}
                      okNote="kpiAchievedLabel" badNote="notMetLabel" badTone={{ c: slCfg.color, bg: "transparent" }} bg="transparent" />
          : null,
        footer: t("targetValueLabel", { value: fmtPct(95) }),
        footerRight: relBadge(hasCurSl ? curSlPct : null, avgSlPct, 95, "floor", fmtPct, (v) => `${n1(v)}%p`),
      };
    })(),
    (() => {
      // 명확한 목표값 없음 → 목표 delta 대신 "전체 N건 중" 컨텍스트 표기.
      const status = dispExcess == null ? "neutral"
                   : dispExcess === 0 ? "good" : dispExcess < 50 ? "warn" : "bad";
      return {
        icon: "", label: t("excessInvCountLabel"),
        value: kpiCnt(dispExcess), unit: t("caseUnitLabel"), status,
        footer:      effTotal > 0 ? t("ipTotalCountAmongLabel", { count: effTotal.toLocaleString() }) : "",
        footerRight: relBadge(hasCurExcess ? curExcessCnt : null, excessCnt, 0, "lower", (v) => fmtCnt(v, t)),
      };
    })(),
    (() => {
      const status = invAmtStatus(dispInvAmt, kpi.total_target_inv_amt);
      // 초과 배지 색을 카드 status 밴드와 일치(good→초록·warn→주황·bad→빨강).
      const aCfg = KPI_STATUS_CFG[status] || KPI_STATUS_CFG.neutral;
      return {
        icon: "", label: t("invHoldingCostAmtLabel"),
        // 통화기호·자릿수 축약(B/M)은 지표의 단위가 아니라 값의 배율이라 unit 슬롯으로 쪼개지 않는다
        value: fmtAmt(dispInvAmt), status,
        valueRight: (hasCurAmt && curInvAmt != null && kpi.total_target_inv_amt)
          ? <GapBadge delta={curInvAmt - kpi.total_target_inv_amt} higherWorse fmt={fmtAmt}
                      okNote="costSavedLabel" badNote="exceededLabel" badTone={{ c: aCfg.color, bg: "transparent" }} bg="transparent" />
          : null,
        footer: kpi.total_target_inv_amt ? t("targetValueLabel", { value: fmtAmt(kpi.total_target_inv_amt) }) : "",
        footerRight: relBadge(hasCurAmt ? curInvAmt : null, relInvAmt ?? null, kpi.total_target_inv_amt || null, "two", fmtAmt),
      };
    })(),
  ];

  return (
    <Stack spacing={1.5}>

      {/* 미집계 빈 상태 패널 — 노란 한 줄 배너를 대체(RP 와 동일 규격).
          IP 는 릴리즈 목표가 없어도 '현재 재고'는 보이지만, 목표 대비 판정이 전부 불가능하다. */}
      {releasedNone && (() => {
        const verName   = cs.plan_ver_name ?? versionInfo?.name ?? null;
        const releaseTs = cs.plan_release_ts ?? versionInfo?.releaseTs ?? null;
        return (
          <StatusEmptyPanel
            title={t("statusNoAggregatedResultTitle")}
            desc={verName
              ? t("statusNoAggregatedResultDesc", { name: verName })
              : t("statusNoReleasedVersionDesc")}
            ctaLabel={t("menuIpTargetInvSimulation")}
            onCta={() => openPlanRunScreen(reduxDispatch, "TARGET_INV_SIMULATION")}
            summary={buildStatusPanelSummary(t, {
              verName,
              releaseTs: releaseTs ? new Date(releaseTs).toLocaleDateString() : null,
              lastRun,
            })}
          />
        );
      })()}

      {/* 현황 KPI — 공용 무배경 카드(흰 배경 + 하단 상태바 + 상태색 숫자) */}
      <StatusKpiSection
        collapsible
        defaultOpen
        versionInfo={versionInfo}
        right={releasedNone ? <StatusPendingTag /> : undefined}
        cards={kpiCards.map(k => {
          const cfg = KPI_STATUS_CFG[k.status] || KPI_STATUS_CFG.neutral;
          return {
            key: k.label,
            label: k.label, value: k.value, unit: k.unit, color: cfg.color,
            valueRight: k.valueRight, footer: k.footer, footerRight: k.footerRight,
          };
        })}
      />

      {/* ── 추이 + 진단 (좌우 2열) ─────────────────────────────────────────
           좌: 월별 재고 실적 vs 목표 추이 (Leaf 품목 계층 드롭다운)
           우: 재고 이슈 진단 — 개선 검토 항목
           ⚠️ 2열 골격은 데이터 유무와 무관하게 고정한다(RP 와 동일 규칙) — 추이 섹션을 지우면
              진단이 전폭으로 튀고 아래가 빈 여백이 된다. 데이터가 없으면 자리를 지키고 스켈레톤. */}
      <Box sx={{ display: "grid", gap: 1.5,
                 gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>

        <StatusSection
          collapsible
          defaultOpen
          collapseDisabled={!invHasData}
          title={t("ipInventoryTrendTitle")}
          right={!invHasData ? <StatusPendingTag /> : undefined}
          cardSx={{ height: "100%" }}
          contentSx={{ p: "14px 15px !important" }}
          // 헤더는 제목 + 접힘 화살표만 — 조회 범위 드롭다운은 본문 최상단 툴바로 내렸다.
          // 추이 데이터가 없으면 고를 범위가 없으므로 툴바를 렌더하지 않는다(동작 불가 컨트롤 제거).
          toolbar={invHasData && invLeafOptions.length > 0 ? (
            <StatusToolbar label={t("trendScopeLabel")}>
              <StatusToolbarSelect
                value={trendGroup}
                onChange={setTrendGroup}
                options={[
                  { value: "", label: t("ipInvGroupAll") },
                  ...invLeafOptions.map(([cd, name]) => ({ value: cd, label: name })),
                ]}
              />
            </StatusToolbar>
          ) : undefined}
        >
          {invHasData ? (
            <ForecastActualChart
              trend={selectedTrend}
              height={chartTrendHeight - trendToolbarH}
              colorMap={{ actual_inv_qty: C.primary, target_inv_qty: C.warning }}
              dashKeys={["target_inv_qty"]}
              emptyText={t("ipInventoryTrendEmpty")}
            />
          ) : (
            <StatusEmpty variant="chart" minHeight={chartTrendHeight}
                         text={t("statusTrendPendingDesc")} />
          )}
        </StatusSection>

        <StatusSection
          title={t("ipIssueDiagnosisTitle")}
          subtitle={t("ipIssueDiagnosisSubtitle")}
          collapsible
          defaultOpen
          collapseDisabled={issueItems.length === 0}
          right={issueItems.length === 0 ? <StatusPendingTag /> : undefined}
          cardSx={{ height: "100%" }}
          contentSx={{ p: "10px 15px !important" }}
        >
          {/* 이 Box 실측 높이로 좌측 차트 높이를 맞춘다(measureIssueBox → ResizeObserver) */}
          <Box ref={measureIssueBox}>
          {issueItems.length === 0 ? (
            // 스켈레톤 3행 — 릴리즈 후 무엇이 채워지는지 예고하고 좌측 차트 높이를 지킨다.
            <StatusEmpty variant="rows" count={3} text={t("MSG_NoStatusIssueData")} />
          ) : (
            // 헤더는 없지만 StatusItemList 로 감싼다 — 행마다 단위가 건/%p/일/% 로 갈리므로
            // 숫자 열·단위 열 폭을 실측 동기화해야 단위 시작점이 세로로 정렬된다.
            // (열 라벨은 붙일 수 없는 리스트다 — bar={false} 인 이유와 같다)
            <StatusItemList>
            <Stack spacing={0.7}>
              {issueItems.map((iss) => {
                const st = ISSUE_STYLE[iss.level];
                return (
                  <StatusItemRow key={iss.title}
                    accent={st.color}
                    bar={false}
                    name={(
                      <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: st.text,
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {iss.title}
                      </Typography>
                    )}
                    sub={iss.desc}
                    // 숫자·단위를 두 열로 — 행마다 단위가 달라(건/%p/일/%) 한 셀에 붙이면 시작점이 튄다
                    cells={[
                      { w: ISSUE_CNT_W, unit: iss.unit, num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: st.color, lineHeight: 1, whiteSpace: "nowrap" }}>
                          {iss.affectedCount}
                        </Typography>
                      ) },
                    ]}
                    rightW={ISSUE_KPI_CHIP_W}
                    right={(
                      <Chip label={iss.kpiLabel} size="small"
                        sx={{ bgcolor: st.bg, color: st.color, border: `0.5px solid ${st.color}55`,
                              fontSize: FONT.CAPTION, height: 18, "& .MuiChip-label": { px: "5px" } }} />
                    )}
                  />
                );
              })}
            </Stack>
            </StatusItemList>
          )}
          </Box>
        </StatusSection>

      </Box>

      {/* 재고 과부족 현황 — 품목 계층별 분포(적정·부족·과잉) + 그 아래 부족·과잉 TOP5 (헤더 클릭 시 통째 접힘)
          RP/MP 가 쓰는 topProblemItemsTitle("문제 항목 TOP 5")을 재사용하지 않는다 — 적정 재고까지
          담게 되어 '문제 항목'이라는 이름이 내용과 어긋나므로 IP 전용 키를 쓴다. */}
      <StatusSection
        title={t("invShortExcessTitle")}
        subtitle={t("invShortExcessSubtitle")}
        collapsible
        defaultOpen
        contentSx={{ p: "10px 15px !important" }}
      >
        {/* 품목 계층별 재고 분포 — 부족·과잉·적정 3개 바를 한 소섹션으로 묶는다.
            3개가 같은 분류 기준(목표 대비 ±band%)을 공유하므로 기준 툴팁은 섹션 제목에 한 번만 단다
            (바마다 달면 동일 문구가 3번 반복된다). 바 제목도 '품목 계층별' 접두어를 섹션이 흡수.
            ⚠️ 순서 계약: 계층(품목군) 분포 → 그 아래 품목 단위 TOP5. 부족·과잉 바의 세그먼트를
            누르면 **바로 아래 같은 열의 리스트**가 그 계층으로 좁혀지므로 좌우 열이 위아래로 짝을 이룬다. */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 1.2 }}>
            <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>
              {t("itemHrchyDistTitle")}
            </Typography>
            <Tooltip title={distBandTip} placement="top" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 13, color: C.text3, cursor: "help" }} />
            </Tooltip>
          </Box>

          {/* ③ 적정 재고 — 좌우 통합 전폭. 대응하는 리스트가 없어(문제 항목이 아니다) 필터 대상이
              아니므로 세그먼트 클릭은 기존 목표재고 시뮬레이션 드릴다운을 그대로 유지한다. */}
          <StatusDistributionBar
            title={t("itemHrchyNormalTitle")}
            subtitle={t("itemHrchyNormalSub")}
            baseColor={C.success}
            /* 미집계면 "적정 0 품목 / 전체 0 품목 · 0.0%" 가 판정처럼 읽힌다 — 요약을 아예 뺀다. */
            summary={releasedNone ? undefined : t("itemHrchyNormalSummary", {
              normal: fmtDistQ(hrchyDist.normal?.total),
              total:  fmtDistQ(hrchyDist.normal?.total_items),
              pct:    (hrchyDist.normal?.pct ?? 0).toFixed(1),
            })}
            segments={distNormal}
            formatValue={fmtDistQ}
            unitLabel={t("item")}
            etcLabel={(n) => t("itemHrchyEtcLabel", { n })}
            onSegmentClick={canDrillSim ? goToSimByCategory : undefined}
            emptyText={t("MSG_NoData")}
          />

          <Box sx={{ mt: 1.8, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5,
                     "& > *": { minWidth: 0 } }}>
            {/* ① 재고 부족 — 클릭 시 아래 '긴급 재고 부족 TOP 5' 를 그 계층으로 필터 */}
            <StatusDistributionBar
              title={t("itemHrchyShortageTitle")}
              subtitle={t("itemHrchyShortageSub")}
              baseColor={C.danger}
              /* 미집계면 "합계 0 EA" 가 실측처럼 읽힌다 — 요약 자체를 빼고 빈 상태만 남긴다. */
              summary={releasedNone ? undefined : t("itemHrchyTotalQty", { qty: fmtDistQ(hrchyDist.shortage?.total) })}
              segments={distShortage}
              formatValue={fmtDistQ}
              unitLabel="EA"
              etcLabel={(n) => t("itemHrchyEtcLabel", { n })}
              onSegmentClick={toggleLeaf(setShortageLeaf)}
              selectedCd={shortageLeaf?.cd}
              emptyText={t("MSG_NoData")}
            />
            {/* ② 과잉재고 — 클릭 시 아래 '과잉재고 TOP 5' 를 그 계층으로 필터 */}
            <StatusDistributionBar
              title={t("itemHrchyExcessTitle")}
              subtitle={t("itemHrchyExcessSub")}
              baseColor={C.warning}
              summary={releasedNone ? undefined : t("itemHrchyTotalQty", { qty: fmtDistQ(hrchyDist.excess?.total) })}
              segments={distExcess}
              formatValue={fmtDistQ}
              unitLabel="EA"
              etcLabel={(n) => t("itemHrchyEtcLabel", { n })}
              onSegmentClick={toggleLeaf(setExcessLeaf)}
              selectedCd={excessLeaf?.cd}
              emptyText={t("MSG_NoData")}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1.8, pt: 1.4, borderTop: `0.5px solid ${C.border}`,
                   display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5,
                   "& > *": { minWidth: 0 } }}>

          {/* 긴급 재고 부족 TOP 5 */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{t("urgentStockoutTop5Title")}</Typography>
              <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("urgentStockoutTop5Sub")}</Typography>
              {leafChip(shortageLeaf, () => setShortageLeaf(null))}
            </Box>
            <StatusItemList>
            {shrtgeItems.length > 0 && (
              <StatusItemHeader
                nameLabel={`${t("item")} / ${t("location")}`}
                rightW={RISK_CHIP_W}
                cells={[
                  { w: 116, label: t("invFulfillmentLabel"), unit: "%" },
                  { w: 78, label: t("shortageAmtLabel") },
                ]} />
            )}
            <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
              {shrtgeItems.map((row, i) => {
                const rc = RISK_COLORS[row.risk_status_cd] || RISK_COLORS.CRITICAL;
                const gap = row.inv_gap_amt;
                // 재고 충족율 = 보유 / 목표 재고일수 (진행바, 0~100 클램프)
                const fillPct = (row.target_inv_days != null && row.target_inv_days > 0 && row.inv_days != null)
                  ? Math.min(Math.max((row.inv_days / row.target_inv_days) * 100, 0), 100) : 0;
                const locText = row.loc_name || row.loc_cd || "";
                return (
                  <StatusItemRow key={`${row.item_cd ?? "?"}-${row.loc_cd ?? i}`}
                    accent={rc.color}
                    name={<NavName targets={[{ label: i18n.t("menuIpTargetInvSimulationResult"), onClick: canDrillSim ? () => goToSimByItem(row) : undefined }]}>{row.item_name || row.item_cd || "—"}</NavName>}
                    sub={locText}
                    /* 현재/목표 수량은 바의 분자·분모 격이라 행에서 빼고 툴팁으로 내린다.
                       바는 재고'일수' 비율이므로 툴팁에 일수 쌍을 함께 실어 수량 쌍과 차원이
                       다르다는 점(수량으로 검산하면 바 길이와 어긋난다)을 드러낸다 */
                    bar={{ value: fillPct, color: rc.color,
                           ariaLabel: `${t("invDaysLabel")} ${(fmtDays(row.inv_days, t) ?? "—")} / ${t("targetInvDays")} ${(fmtDays(row.target_inv_days, t) ?? "—")}`,
                           tip: (
                             <BarTip title={`${row.item_name || row.item_cd || "—"}${locText ? ` · ${locText}` : ""}`}
                               rows={[
                                 { color: rc.color, label: t("invDaysLabel"), value: (fmtDays(row.inv_days, t) ?? "—") },
                                 {                  label: t("targetInvDays"), value: (fmtDays(row.target_inv_days, t) ?? "—") },
                                 { color: rc.color, label: t("currentInvQty"), value: fmtQ(row.current_inv_qty) },
                                 {                  label: t("targetInvQty"),  value: fmtQ(row.target_inv_qty) },
                               ]} />
                           ) }}
                    cells={[
                      { w: 116, unit: "%", num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: rc.color, lineHeight: 1 }}>{fillPct.toFixed(0)}</Typography>
                      ) },
                      { w: 78, node: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 800, color: rc.color, lineHeight: 1 }}>
                          {gap != null && gap < 0 ? `${fmtAmt(Math.abs(gap))}` : "—"}
                        </Typography>
                      ) },
                    ]}
                    rightW={RISK_CHIP_W}
                    right={(
                      <Chip
                        label={RISK_LABEL[row.risk_status_cd] ? t(RISK_LABEL[row.risk_status_cd]) : (row.risk_status_cd || "—")}
                        size="small"
                        sx={{ bgcolor: rc.bg, color: rc.color, border: `0.5px solid ${rc.color}55`,
                              fontSize: FONT.CAPTION, height: 19, "& .MuiChip-label": { px: "5px" }, flexShrink: 0 }}
                      />
                    )} />
                );
              })}
              {/* 초록 좌측바(=문제 없음)를 쓰지 않는다 — 집계가 없는 구간은 판정 불가다.
                  계층 선택 중 0건은 "데이터 없음"이 아니라 "그 계층에 해당 항목이 없음"이다
                  (분포 바는 릴리즈 기준, 리스트는 최신 실적 기준이라 실제로 발생할 수 있다). */}
              {shrtgeItems.length === 0 && (
                <StatusEmpty text={shortageLeaf ? t("MSG_IpNoItemsInHrchy") : undefined} />
              )}
            </Stack>
            </StatusItemList>
          </Box>

          {/* 과잉재고 TOP 5 */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{t("excessInvTop5Title")}</Typography>
              <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("excessInvTop5Sub")}</Typography>
              {leafChip(excessLeaf, () => setExcessLeaf(null))}
            </Box>
            <StatusItemList>
            {excessItems.length > 0 && (
              <StatusItemHeader
                nameLabel={`${t("item")} / ${t("location")}`}
                rightW={RISK_CHIP_W}
                cells={[
                  { w: 56, label: t("invNormalShareLabel"), unit: "%" },
                  { w: 66, label: t("excessAmtLabel") },
                ]} />
            )}
            <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
              {excessItems.map((row, i) => {
                const rc = RISK_COLORS[row.risk_status_cd] || RISK_COLORS.HIGH;
                const gap = row.inv_gap_amt;
                // 초과 일수 — 목표 대비 며칠치 재고가 남는지(엔진 excess_days).
                //   수요가 없는 품목(sales_qty_avg=0)은 0으로 내려오므로 "정상"으로 오독되지 않게 "—" 처리.
                const exDays = row.excess_days;
                const locText = row.loc_name || row.loc_cd || "";
                // 적정 비중 = 목표 / 보유 재고일수. 과잉은 현재>목표로 **역전**돼 있으므로
                // 부족 쪽 충족율(현재/목표)의 분자·분모를 뒤집으면 다시 0~100% 스케일이 성립한다.
                //   채운 부분 = 적정 소요분, 빈 부분 = 과잉분.
                //   목표 없음(target<=0)·수요 0(inv_days<=0) → 0% = 전량 과잉. 의도된 표현이다.
                const normalPct = (row.inv_days > 0 && row.target_inv_days > 0)
                  ? Math.min(Math.max((row.target_inv_days / row.inv_days) * 100, 0), 100) : 0;
                return (
                  <StatusItemRow key={`${row.item_cd ?? "?"}-${row.loc_cd ?? i}`}
                    accent={rc.color}
                    name={<NavName targets={[{ label: i18n.t("menuIpTargetInvSimulationResult"), onClick: canDrillSim ? () => goToSimByItem(row) : undefined }]}>{row.item_name || row.item_cd || "—"}</NavName>}
                    sub={locText}
                    /* 부족 행과 동일 규격의 진행바. 현재/목표 수량은 바의 분자·분모 격이라
                       행에서 빼고 툴팁으로 내린다(부족 쪽과 표기 규칙을 맞춘다).
                       ⚠️ 헤더와 bar 구성이 어긋나면 열이 밀린다 — 헤더도 bar 열을 갖는다 */
                    bar={{ value: normalPct, color: rc.color,
                           ariaLabel: `${t("targetInvDays")} ${(fmtDays(row.target_inv_days, t) ?? "—")} / ${t("invDaysLabel")} ${(fmtDays(row.inv_days, t) ?? "—")}`,
                           tip: (
                             <BarTip title={`${row.item_name || row.item_cd || "—"}${locText ? ` · ${locText}` : ""}`}
                               rows={[
                                 { color: rc.color, label: t("invDaysLabel"), value: (fmtDays(row.inv_days, t) ?? "—") },
                                 {                  label: t("targetInvDays"), value: (fmtDays(row.target_inv_days, t) ?? "—") },
                                 { color: rc.color, label: t("currentInvQty"), value: fmtQ(row.current_inv_qty) },
                                 {                  label: t("targetInvQty"),  value: fmtQ(row.target_inv_qty) },
                                 { color: rc.color, label: t("excessDaysLabel"),
                                   value: exDays != null && exDays > 0
                                     ? `+${Math.round(exDays)}${t("dayUnitShort")}` : "—" },
                               ]} />
                           ) }}
                    cells={[
                      { w: 56, unit: "%", num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: rc.color, lineHeight: 1 }}>{normalPct.toFixed(0)}</Typography>
                      ) },
                      { w: 66, node: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 800, color: rc.color, lineHeight: 1 }}>
                          {gap != null && gap > 0 ? `${fmtAmt(gap)}` : "—"}
                        </Typography>
                      ) },
                    ]}
                    rightW={RISK_CHIP_W}
                    right={(
                      <Chip
                        label={RISK_LABEL[row.risk_status_cd] ? t(RISK_LABEL[row.risk_status_cd]) : (row.risk_status_cd || "—")}
                        size="small"
                        sx={{ bgcolor: rc.bg, color: rc.color, border: `0.5px solid ${rc.color}55`,
                              fontSize: FONT.CAPTION, height: 19, "& .MuiChip-label": { px: "5px" }, flexShrink: 0 }}
                      />
                    )} />
                );
              })}
              {excessItems.length === 0 && (
                <StatusEmpty text={excessLeaf ? t("MSG_IpNoItemsInHrchy") : undefined} />
              )}
            </Stack>
            </StatusItemList>
          </Box>

        </Box>
      </StatusSection>

    </Stack>
  );
}

// ── IP 모듈 설정 ───────────────────────────────────────────────────────────
export const IP_MODULE = {
  code: "IP",
  // ── 비교 탭 "품목 수준 차이"(CompareItemDiff) 설정 — 컬럼·드릴다운 주입 ──────
  // dir: delta 색상 판정 방향("up"=높을수록 좋음). excess_days 는 목표 대비 과부족
  //   (음수=부족/양수=과잉, 이상값 0)이라 dir 대신 kind:"gap" 상태 라벨 렌더를 쓴다.
  //   백엔드 판정축(primary)=excess_days(toward_zero)라 맨 앞에 배치.
  itemDiffCfg: {
    locKey: "loc_cd",
    locNameKey: "loc_name",   // 거점명 표시(백엔드 loc_name 패스스루) — 없으면 코드 폴백
    // 변경 중심 뷰 — 과부족(판정축) · 현재고(불변 기준, single) · 목표재고(정책 변동치).
    columns: [
      { key: "excess_days",     labelKey: "itemDiffColExcessDays", ko: "목표대비 과부족(일)", digits: 1, kind: "gap" },
      // single: onHand 실적 기반 단일 값이라 셀 내용이 짧음 — 트랙을 좁혀(gridWidth) 남는
      //   폭을 왼쪽 위험도·과부족 열에 양보(위험도 전이 텍스트와 gap 텍스트 겹침 완화).
      { key: "current_inv_qty", labelKey: "itemDiffColCurInv",     ko: "현재고(EA)",   digits: 0, dir: null,
        single: true, gridWidth: "minmax(64px, 0.6fr)" },
      // 목표재고: 정책 산출값 — 높낮이 자체는 좋고 나쁨이 아니라 중립색(변동치만 표기).
      { key: "target_inv_qty",  labelKey: "itemDiffColTargetInv",  ko: "목표재고(EA)", digits: 0, dir: null },
    ],
    // 행별 분류 근거 배지 — 위험도 전이가 없을 때 primary(excess_days, 목표대비 과부족) 표기용 축약 라벨.
    primaryReason: { labelKey: "itemDiffReasonGapShort", ko: "과부족", unit: "일" },
    // 품목명 클릭 → 목표재고 시뮬레이션 결과 (비교 시나리오 버전 + 거점/품목 필터 주입)
    makeDrillTargets: (dispatch, row, version) => [{
      label: i18n.t("menuIpTargetInvSimulationResult"),
      onClick: () => openTargetInvSimulationResults(
        dispatch, { locCd: row.loc_cd, itemCd: row.item_cd }, version),

    }],
  },
  // ── 비교 탭 산점도/핵심차이 카드 클릭 → 해당 시나리오 버전의 목표재고 시뮬 결과 오픈(필터 없이 전체) ──
  scenarioDrill: (dispatch, version) => openTargetInvSimulationResults(dispatch, {}, version),
  // ── 시나리오 구성 탭 "예상 효과" 미리보기 필드(엔진 실행 전) — expected_effects 키 매핑 ──
  //   goodSign: +1=값이 클수록 개선(녹색 g1), -1=값이 작을수록 개선. badColor: 악화 시 색(g2 경고/g3 위험).
  //   unitKey=i18n 단위 키(번역 필요), unit=리터럴 단위(%, %p). isString: 백엔드가 "≈ -2.0%p" 문자열로 내려주는 필드.
  expectedEffectsFields: [
    { key: "inv_days_change",           labelKey: "invDaysColonLabel",      unitKey: "dayUnit", goodSign: -1, badColor: "g2" },
    { key: "target_inv_amt_change_pct", labelKey: "targetInvAmtColonLabel", unit: "%",          goodSign: -1, badColor: "g2" },
    { key: "effective_sl_pct_change",   labelKey: "serviceLevelColonLabel", unit: "%p",         goodSign: 1,  badColor: "g3", isString: true },
  ],
  // ── Analysis 탭 Quick Prompt (자유 질문, 액션 없음) — { i18nKey, ko } ──────────
  quickPrompts: {
    analysis: {
      0: [
        { i18nKey: "agentAskIpKpi",     ko: "현황 KPI 해석해줘" },
        { i18nKey: "agentAskIpSlMiss",  ko: "서비스레벨 미달 품목은?" },
        { i18nKey: "agentAskIpExcess",  ko: "과잉재고 품목 알려줘" },
      ],
      1: [
        { i18nKey: "agentAskIpRaiseSl",  ko: "서비스레벨 높이려면?" },
        { i18nKey: "agentAskIpSsImpact", ko: "안전재고 배율 영향은?" },
        { i18nKey: "agentAskIpOrderFreq",ko: "발주 주기 차이는?" },
      ],
      2: [
        { i18nKey: "agentAskIpBest",     ko: "어떤 시나리오가 최적이야?" },
        { i18nKey: "agentAskIpTradeoff", ko: "재고일수 vs 서비스레벨" },
      ],
      3: [
        { i18nKey: "agentAskIpChecklist", ko: "확정 전 체크리스트" },
      ],
    },
  },

  // ── Action 탭: 화면 하위상태(step·mode·aiResult)에 맞는 "지금 가능한 액션"만 선언 (DP 와 동일 패턴) ──
  // 값 고정 set_param 프리셋은 clickOnly(자유입력 dispatch 제외). 자유입력 파라미터 변경은 set_param 값 추출 경로가 처리.
  agentActions: ({ step, mode, aiResult, selectedRole, confirmed, exec }) => {
    // agentActions는 모듈 레벨 함수(컴포넌트 아님)라 useTranslation 훅을 쓸 수 없어
    // i18n 싱글턴 인스턴스로 직접 번역한다(항상 현재 언어를 반영).
    const selMode = (m, key, ko) => {
      const modeLabel = i18n.t(key, { defaultValue: ko });
      return {
        id: `ip.mode.${m}`, i18nKey: key, ko, group: "action", style: "secondary",
        description: i18n.t("ipSelectModeDesc", { mode: modeLabel }),
        run: () => exec({ action: "select_mode", payload: { mode: m } }),
        confirmKo: i18n.t("ipSelectModeConfirm", { mode: modeLabel }),
      };
    };
    // 값이 고정된 파라미터 프리셋(서비스레벨 ±2%p 등)은 제거했다 — 그 수치의 근거가 없고
    //   사용자가 원하는 값이라는 보장도 없다. 파라미터 변경은 "/파라미터 값 변경" 또는 자유발화가
    //   시나리오→항목→값(선택지·직접 입력) 연속 선택으로 받는다.
    const runEngine = {
      id: "ip.engine.run", i18nKey: "agentActIpRunEngine", ko: "엔진 실행",
      group: "action", style: "primary", description: i18n.t("ipRunEngineDesc"),
      run: () => exec({ action: "run_engine" }),
      confirmKo: i18n.t("MSG_IpEngineRunConfirm"),
    };
    // 단계 이동은 컨테이너 generic nav(nav.step*)가 전담 — 모듈별 고정 navTo 는 라벨만 다른
    //   중복("확정 단계로 이동" vs "재고 정책 확정(으)로 이동")이라 제거.
    // 시나리오 선택 칩 — 컨테이너가 엔진 결과 기반으로 동적 등록(scn.select.*, 실제 시나리오명).
    //   고정 base/alt1/alt2 칩은 결과 없는 시나리오도 "선택 성공"으로 보이던 문제로 제거.
    // 확정·릴리즈 — 컨테이너 공통 scn.confirm(전 모듈, 선택+미확정 시에만 노출)이 제공 — 중복 등록 제거.
    if (step === 0) return [
      selMode("ai",     "agentActIpModeAi",     "AI 추천 시나리오 생성"),
      selMode("custom", "agentActIpModeCustom", "표준 시나리오 선택"),
    ];
    if (step === 1) {
      // mode 미설정(null)은 표준 시나리오 선택 상태와 동일(통합 뷰 기본) — 초기 방식 선택 액션 없이
      //   표준 경로 액션을 그대로 노출. AI 전환은 컨테이너 공통 scn.mode.ai 가 제공.
      if (mode === "ai" && !aiResult) return [];
      // 파라미터 변경은 컨테이너 공통 scn.param.change(연속 선택)가, 되돌리기는 slash.reset 이 제공.
      return [runEngine];
    }
    return [];   // 단계 이동은 컨테이너 generic nav 가, 확정은 공통 scn.confirm 이 제공
  },
  label: "ipModuleLabel",
  Icon: Inventory2Icon,
  step2lbl: "ipStep2Label",
  step4lbl: "ipStep4Label",
  inputFrom: "DP",
  skipKpiSection: true,

  initInputs: ["ipInitInputReleased", "ipInitInputIspKpi", "ipInitInputPolicy", "ipInitInputBfMape"],

  // scenarios: Tab1/3/4(현황·비교·확정) 표시용 기준 시나리오 형태. 엔진 결과(agentResults)가
  // 채워지기 전 KPI 비교/확정 화면의 셰이프 폴백으로 쓰인다.
  // (직접 입력 시나리오의 편집 파라미터는 fetchTemplates → param_defs 합집합(paramDefsUnion)에서 오며,
  //  아래 params 배열의 레거시 목업 편집 파라미터는 제거됨.)
  scenarios: [
    {
      role: "base", name: "scnIpBaseName",
      desc: "scnIpBaseDesc",
      tags: ["scnTagKeepCurrent"],
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-", e: "-", f: "-", g: "-" },
    },
    {
      role: "alt1", name: "scnIpAlt1Name",
      desc: "scnIpAlt1Desc",
      tags: ["scnTagServiceLevelImprove", "scnTagSsIncrease"],
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-", e: "-", f: "-", g: "-" },
    },
    {
      role: "alt2", name: "scnIpAlt2Name",
      desc: "scnIpAlt2Desc",
      tags: ["scnTagInventoryEfficiency", "scnTagCostSaving"],
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-", e: "-", f: "-", g: "-" },
    },
  ],
  // 표시 순서: 재고일수군(재고일수·회전률·갭) → 서비스/리스크군(서비스레벨·결품·과잉) → 비용
  // 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t(lbl) 형태로 변환한다.
  kpiLabels: ["avgInvDaysLabel", "invTurnoverRateLabel", "invDaysGapLabel", "serviceLevelSpacedLabel", "stockoutCountLabel", "excessInvCountLabel", "targetInvAmt"],
  kpiKeys:   ["a", "f", "g", "b", "e", "c", "d"],
  bestMap:   { a: "alt2", b: "alt1", c: "alt2", d: "alt2", e: "alt2", f: "alt1", g: "alt1" },
  // 최적/증감 색상 방향. g(재고일수 갭)는 0근접이 이상이라 min/max 미해당 → neutral(강조·색상 제외).
  kpiDir:    { a: "min", b: "max", c: "min", d: "min", e: "min", f: "max", g: "neutral" },
  moneyKpiKeys: ["d"],
  // kpiKeys 와 동일 순서 유지(재고일수군 → 서비스/리스크군 → 비용).
  // label/insight/note 값은 i18n 키 문자열 — 소비처(StepKpiAnalysis)에서 t()로 변환한다.
  kpiMeta: [
    {
      key: "a", icon: "", label: "avgInvDaysLabel",
      getStatus: () => "neutral",
      insight: "ipKpiInsightAvgInvDays",
      note: "lowerIsBetterNote",
    },
    {
      key: "f", icon: "", label: "invTurnoverRateLabel",
      getStatus: (v) => {
        if (v === "-" || v === "—") return "neutral";
        const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
        if (isNaN(n)) return "neutral";
        return n >= 12 ? "good" : n >= 6 ? "near" : n >= 4 ? "warn" : "bad";
      },
      insight: "ipKpiInsightInvTurnover",
      note: "higherIsBetterNote",
    },
    {
      key: "g", icon: "", label: "invDaysGapLabel",
      getStatus: () => "neutral",
      insight: "ipKpiInsightInvDaysGap",
      note: "closerToZeroIsBetterNote",
    },
    {
      key: "b", icon: "", label: "serviceLevelSpacedLabel",
      getStatus: (v) => {
        const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
        if (isNaN(n)) return "neutral";
        return n >= 95 ? "good" : n >= 90 ? "near" : n >= 85 ? "warn" : "bad";
      },
      insight: "ipKpiInsightServiceLevel",
      note: "ipTargetSl95PlusNote",
    },
    {
      key: "e", icon: "", label: "stockoutCountLabel",
      getStatus: (v) => {
        if (v === "-" || v === "—") return "neutral";
        const n = parseInt(String(v).replace(/[^0-9]/g, ""), 10);
        if (isNaN(n)) return "neutral";
        return n === 0 ? "good" : n < 20 ? "warn" : "bad";
      },
      insight: "ipKpiInsightStockoutCount",
      note: "fewerIsBetterNote",
    },
    {
      key: "c", icon: "", label: "excessInvCountLabel",
      getStatus: (v) => {
        if (v === "-" || v === "—") return "neutral";
        const n = parseInt(String(v).replace(/[^0-9]/g, ""), 10);
        if (isNaN(n)) return "neutral";
        return n === 0 ? "good" : n < 50 ? "warn" : "bad";
      },
      insight: "ipKpiInsightExcessCount",
      note: "fewerIsBetterNote",
    },
    {
      key: "d", icon: "", label: "targetInvAmt",
      getStatus: () => "neutral",
      insight: "ipKpiInsightInvHoldingCost",
      note: "lowerIsBetterNote",
    },
  ],
  //   flowTopNodes = 공통 3(현황·시나리오 확정·KPI 해석) + 분기 앵커 1.
  engineTrack: { parentTool: "ip_step3_engine", childPrefix: "ip_engine_",
                 match: "role", flowTopNodes: 4 },
  // 값은 i18n 키 문자열 — 현재 소비처 없음(mod.enginePhases 미사용, SSE 실시간 단계로 대체됨). 향후 대비 키만 등록.
  enginePhases: [
    "ipEnginePhaseLoading",
    "ipEnginePhasePolicyAnalysis",
    "ipEnginePhaseAiGenerate",
    "ipEnginePhaseScenario1Run",
    "ipEnginePhaseScenario2Run",
    "ipEnginePhaseKpiAggregate",
    "complete",
  ],
  aiRec: 0,

  // AI 추천 시나리오 안내 — IP는 재고일수(현재/목표)·서비스레벨·리드타임·재고 정책을 분석하여
  //   재고정책(서비스레벨·안전재고·주기재고) 시나리오를 추천한다. BF 예측 정확도(MAPE)는 참고만 하고 조정 대상 아님.
  // 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  aiRecCardDesc: "ipAiRecCardDesc",
  aiRecIntroLines: [
    "ipAiRecIntroLine1",
    "ipAiRecIntroLine2",
  ],
  aiRecFactors: ["ipAiRecFactorInvDays", "serviceLevelSpacedLabel", "ipAiRecFactorLeadTime", "ipAiRecFactorSsCsPolicy"],
  aiRecLoadingDesc: "ipAiRecLoadingDesc",
  // agentActIpModeAi 의 ko("AI 추천 시나리오 생성")와 동일 문구라 동일 키를 재사용한다.
  aiRecButtonLabel: "agentActIpModeAi",
};

// IP_MODULE에 ExtraInitContent 등록
IP_MODULE.ExtraInitContent = IpExtraInitContent;

// ── 실현 가능성 경고 (비교 탭) — 백엔드 결정형 판정(scn.feasibility_warnings)을 공용 위젯이 렌더.
//   IP 는 R-IP-01(서비스레벨 조정 미적용)·R-IP-02(하향)·R-IP-03(추가 재고 투자) 3종.
IP_MODULE.feasibilityCfg = { sectionTitleKey: "feasWarnSectionTitle" };
IP_MODULE.scenarioWarnCount = feasWarnCount;   // KPI 비교표 '경고' 행(공용 슬롯, tone 만 전달)
IP_MODULE.RecHeroBadges = FeasibilityHeroBadges;

// 산업별 표준 시나리오 (시나리오 구성 탭 — 표준 시나리오 모드). 공통 화면이 mod.templates 로 참조.
IP_MODULE.templates = [
  {
    id: "ip_standard",
    industry: "standardTagLabel",
    name: "ipStandardPolicyTemplateName",
    useCase: "ipStandardPolicyUseCase",
    desc: "ipStandardPolicyDesc",
    tags: ["standardTagLabel", "ipServiceLevel95TagLabel", "ipDynamicSsTagLabel"],
    // ⚠ scenarios[].name/desc 는 시나리오 필드 전체 제외 정책(사용자 결정)에 따라 원문 유지.
    scenarios: [
      { role: "base", name: "SS 1.5x", desc: "서비스 수준 95%, SS 배율 1.5x.",
        params: { service_level: 95, ss_multiplier: 1.5 } },
      { role: "alt1", name: "린 SS 1.0x", desc: "재고 최소화, SS 배율 1.0x.",
        params: { service_level: 93, ss_multiplier: 1.0 } },
      { role: "alt2", name: "완충 SS 2.0x", desc: "리스크 완충, SS 배율 2.0x.",
        params: { service_level: 97, ss_multiplier: 2.0 } },
    ],
  },
];

// 표준 시나리오 동적 로딩 (z_ai_scenario 카탈로그 — 기존 하드코딩 templates 대체).
// 공통 StepScenarioCreate 가 mod.fetchTemplates 존재 시 이 결과를 템플릿 카드로 렌더한다.
// 각 변형(base/alt1/alt2)은 param_defs·overrides 를 임베드 → 공통 ScenarioParamDefsFields 가
// 동적 렌더한다. 직접 입력 카드는 이 변형들의 param_defs 합집합(paramDefsUnion)을 빈 값으로 노출.
IP_MODULE.fetchTemplates = async () => {
  const res = await scenarioService.getScenarios("IP");
  rememberModuleCatalog(IP_MODULE, res);   // 표준 시나리오 0건이어도 직접 입력 카드가 그려지도록 최상위 카탈로그 보관
  const problemTargets = res?.problem_targets || {};
  return (res?.scenarios || []).map(scn => ({
    id:          scn.scenario_cd,
    industry:    scn.source_type === "USER" ? "userTagLabel"
                 : (scn.recommended_flg === "Y" ? "recommendedTagLabel" : "standardTagLabel"),
    // 표시용 i18n 키(*_key)를 우선 사용, 없으면 원문 폴백. LLM 은 원문(scenario_name 등)을 그대로 소비.
    name:        scn.scenario_name_key || scn.scenario_name,
    direction:   scn.scenario_direction_key || scn.scenario_direction,
    useCase:     scn.desc_txt_key || scn.desc_txt,
    desc:        scn.desc_txt_key || scn.desc_txt,
    recommended: scn.recommended_flg === "Y",
    // base/alt1/alt2 — overrides + param_defs 임베드. problem_targets(거점/품목 후보) 주입.
    scenarios:   (scn.variants || []).map(v => ({ ...v, problem_targets: problemTargets })),
  }));
};

// ── IP 시나리오 플래닝 페이지 ──────────────────────────────────────────────
const AiScenarioPlanningLazy = React.lazy(() => import("./AiScenarioPlanning"));

export default function AiIpScenarioPlanning({ viewName }) {
  return (
    <React.Suspense fallback={null}>
      <AiScenarioPlanningLazy viewName={viewName} singleModuleCode="IP" />
    </React.Suspense>
  );
}


